import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { EVENTO_LINKEDIN_MUDOU, linkedinService } from '../linkedinService';

// Os contadores do menu (Prospecção → "Conversas LinkedIn" e "Aprovar e enviar"). Cada um lê o
// total do próprio recorte a cada minuto com a aba visível, quando a janela volta ao foco e quando
// uma tela do LinkedIn avisa que mudou algo (aprovou, pulou, abriu uma conversa). Falha é silêncio.

const INTERVALO_MS = 60_000;

const useContador = (ler: () => Promise<number>): number => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let vivo = true;
    const atualizar = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      ler()
        .then((v) => {
          if (vivo) setN(v);
        })
        .catch(() => {
          if (vivo) setN(0);
        });
    };
    atualizar();
    const t = window.setInterval(atualizar, INTERVALO_MS);
    window.addEventListener('focus', atualizar);
    window.addEventListener(EVENTO_LINKEDIN_MUDOU, atualizar);
    return () => {
      vivo = false;
      window.clearInterval(t);
      window.removeEventListener('focus', atualizar);
      window.removeEventListener(EVENTO_LINKEDIN_MUDOU, atualizar);
    };
  }, [ler]);
  return n;
};

const Numero = ({ n, rotulo, cor }: { n: number; rotulo: string; cor: 'warning' | 'primary' }) =>
  n > 0 ? (
    <Box
      component="span"
      aria-label={`${n} ${rotulo}`}
      sx={{
        ml: 1,
        minWidth: 20,
        height: 20,
        px: 0.75,
        borderRadius: 10,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 600,
        bgcolor: `${cor}.main`,
        color: `${cor}.contrastText`,
        flexShrink: 0
      }}
    >
      {n > 99 ? '99+' : n}
    </Box>
  ) : null;

// Quem não conectou o LinkedIn recebe 404 de /conversas e /comandos, e o navegador registra cada
// 404 como erro no console, em toda tela e a cada minuto. Pergunta antes pela conexão (204 = sem
// conta, sem erro), uma vez por ciclo para os dois contadores, e só então lê os números.
let temContaCache: { em: number; valor: Promise<boolean> } | null = null;
const temConta = (): Promise<boolean> => {
  const agora = Date.now();
  if (!temContaCache || agora - temContaCache.em > INTERVALO_MS / 2) {
    temContaCache = {
      em: agora,
      valor: linkedinService
        .minhaConta()
        .then((c) => c != null)
        .catch(() => false)
    };
  }
  return temContaCache.valor;
};
if (typeof window !== 'undefined') {
  window.addEventListener(EVENTO_LINKEDIN_MUDOU, () => {
    temContaCache = null;
  });
}

const lerAprovacoes = async () => ((await temConta()) ? (await linkedinService.comandos({ status: 'AGUARDANDO_APROVACAO' })).length : 0);
const lerNaoLidas = async () => ((await temConta()) ? linkedinService.contarConversas({ naoLidas: true }) : 0);

/** Envios assistidos esperando o vendedor aprovar. */
export const ContadorAprovacoes = () => <Numero n={useContador(lerAprovacoes)} rotulo="aguardando aprovação" cor="warning" />;

/** Conversas com mensagem que ninguém abriu. */
export const ContadorNaoLidas = () => <Numero n={useContador(lerNaoLidas)} rotulo="conversas não lidas" cor="primary" />;
